/** Planned lump-sum super contributions. Max 10 per scenario. */
import { FinancialConfig } from './FinancialConfig.js';

export const MAX_LUMP_SUM_CONTRIBUTIONS = 10;

export const CONTRIBUTION_TYPES = {
  NON_CONCESSIONAL: 'nonConcessional',
  CONCESSIONAL: 'concessional',
  DOWNSIZER: 'downsizer',
  SPOUSE: 'spouse',
};

export const CONTRIBUTION_RECIPIENTS = {
  YOU: 'you',
  PARTNER: 'partner',
  SPLIT: 'split',
};

const emptyRow = () => ({
  recipient: CONTRIBUTION_RECIPIENTS.YOU,
  contributionType: CONTRIBUTION_TYPES.NON_CONCESSIONAL,
  scheduleType: 'year',
  scheduleValue: '',
  amount: '',
  label: '',
});

export const defaultLumpSumContributionRows = () =>
  Array.from({ length: MAX_LUMP_SUM_CONTRIBUTIONS }, emptyRow);

export function normalizeLumpSumContributionRows(raw) {
  const defaults = defaultLumpSumContributionRows();
  if (!Array.isArray(raw)) return defaults;
  return defaults.map((empty, idx) => {
    const row = raw[idx];
    if (!row || typeof row !== 'object') return { ...empty };
    return {
      recipient: Object.values(CONTRIBUTION_RECIPIENTS).includes(row.recipient) ? row.recipient : empty.recipient,
      contributionType: Object.values(CONTRIBUTION_TYPES).includes(row.contributionType) ? row.contributionType : empty.contributionType,
      scheduleType: row.scheduleType === 'age' ? 'age' : 'year',
      scheduleValue: row.scheduleValue ?? '',
      amount: row.amount ?? '',
      label: row.label ?? '',
    };
  });
}

export function parseLumpSumContributions(inputs) {
  if (!inputs) return [];
  const rows = Array.isArray(inputs.parsedLumpSumContributions)
    ? inputs.parsedLumpSumContributions
    : inputs.lumpSumContributions;
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => ({
      recipient: Object.values(CONTRIBUTION_RECIPIENTS).includes(row.recipient) ? row.recipient : CONTRIBUTION_RECIPIENTS.YOU,
      contributionType: Object.values(CONTRIBUTION_TYPES).includes(row.contributionType) ? row.contributionType : CONTRIBUTION_TYPES.NON_CONCESSIONAL,
      scheduleType: row.scheduleType === 'age' ? 'age' : 'year',
      scheduleValue: Number(row.scheduleValue),
      amount: Number(row.amount) || 0,
      label: String(row.label || '').trim(),
    }))
    .filter((row) => row.amount > 0 && Number.isFinite(row.scheduleValue) && row.scheduleValue > 0)
    .slice(0, MAX_LUMP_SUM_CONTRIBUTIONS);
}

export function hasLumpSumContributions(inputs) {
  return parseLumpSumContributions(inputs).length > 0;
}

export function isLumpSumContributionRowTouched(row) {
  return String(row?.scheduleValue ?? '').trim() !== ''
    || String(row?.amount ?? '').trim() !== ''
    || String(row?.label ?? '').trim() !== '';
}

export function getVisibleLumpSumContributionRows(rawRows) {
  const rows = normalizeLumpSumContributionRows(rawRows);
  const lastTouchedIndex = rows.reduce((last, row, idx) => (isLumpSumContributionRowTouched(row) ? idx : last), -1);
  const visibleCount = Math.min(MAX_LUMP_SUM_CONTRIBUTIONS, Math.max(1, lastTouchedIndex + 2));
  return rows.slice(0, visibleCount).map((row, idx) => ({ row, idx }));
}

export function coerceLumpSumContributionRowsForProjection(rawRows, { yourAge, partnerAge, periodLength }) {
  const rows = normalizeLumpSumContributionRows(rawRows);
  const minCurrentAge = Math.min(Number(yourAge) || Infinity, Number(partnerAge ?? yourAge) || Infinity);
  const years = Math.max(0, Number(periodLength) || 0);
  return rows.map((row) => {
    const scheduleValue = Number(row.scheduleValue);
    const amount = Number(row.amount) || 0;
    if (
      row.scheduleType === 'age'
      && amount > 0
      && Number.isFinite(scheduleValue)
      && scheduleValue > 0
      && scheduleValue <= years
      && scheduleValue < minCurrentAge
    ) {
      return { ...row, scheduleType: 'year' };
    }
    return row;
  });
}

/** Age rows fire at the recipient's age; split rows use your age so they fire once. */
const firesInYear = (row, { yearIndex, yourAge, partnerAge }) => {
  const planYear = yearIndex + 1;
  if (row.scheduleType === 'year') return Number(row.scheduleValue) === planYear;
  const baseAge = row.recipient === CONTRIBUTION_RECIPIENTS.PARTNER
    ? Number(partnerAge ?? yourAge)
    : Number(yourAge);
  return Number(row.scheduleValue) === baseAge + yearIndex;
};

/** Zero-based projection year in which a contribution row fires (null if it never does). */
export function lumpSumContributionYearIndex(row, { yourAge, partnerAge }) {
  if (row.scheduleType === 'year') {
    const planYear = Number(row.scheduleValue);
    return Number.isFinite(planYear) && planYear >= 1 ? planYear - 1 : null;
  }
  const baseAge = row.recipient === CONTRIBUTION_RECIPIENTS.PARTNER
    ? Number(partnerAge ?? yourAge)
    : Number(yourAge);
  const idx = Number(row.scheduleValue) - baseAge;
  return Number.isFinite(idx) && idx >= 0 ? idx : null;
}

export function getLumpSumContributionsForYear(contributions, { yearIndex, yourAge, partnerAge, isCouple }) {
  if (!contributions?.length) return [];
  const out = [];
  for (const row of contributions) {
    if (!firesInYear(row, { yearIndex, yourAge, partnerAge })) continue;
    if (row.recipient === CONTRIBUTION_RECIPIENTS.SPLIT && isCouple) {
      out.push({ ...row, recipient: CONTRIBUTION_RECIPIENTS.YOU, amount: row.amount / 2 });
      out.push({ ...row, recipient: CONTRIBUTION_RECIPIENTS.PARTNER, amount: row.amount / 2 });
    } else {
      out.push({ ...row, recipient: row.recipient === CONTRIBUTION_RECIPIENTS.PARTNER && isCouple ? CONTRIBUTION_RECIPIENTS.PARTNER : CONTRIBUTION_RECIPIENTS.YOU });
    }
  }
  return out;
}

export function getLumpSumContributionScheduleDiagnostics(inputs, { yourAge, partnerAge, periodLength }) {
  const rows = coerceLumpSumContributionRowsForProjection(inputs?.lumpSumContributions, { yourAge, partnerAge, periodLength });
  const parsed = parseLumpSumContributions({ ...inputs, lumpSumContributions: rows });
  const years = Math.max(0, Number(periodLength) || 0);
  const firingYears = [];
  for (let i = 0; i < years; i++) {
    const rowsForYear = getLumpSumContributionsForYear(parsed, { yearIndex: i, yourAge, partnerAge, isCouple: true });
    const amount = rowsForYear.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
    if (amount > 0) firingYears.push({ planYear: i + 1, amount, rows: rowsForYear });
  }
  const inertRows = parsed.filter((row) => {
    for (let i = 0; i < years; i++) {
      if (firesInYear(row, { yearIndex: i, yourAge, partnerAge })) return false;
    }
    return true;
  });
  return { parsed, firingYears, inertRows };
}

const formatMember = (member) => (member === CONTRIBUTION_RECIPIENTS.PARTNER ? 'Partner' : 'You');

export function summarizeYearContributions({
  scheduledContributions = [],
  sgA = 0,
  sgB = 0,
  yourAge,
  partnerAge,
  balA = 0,
  balB = 0,
  isCouple = false,
}) {
  const caps = FinancialConfig.CONTRIBUTION_CAPS;
  const concessionalCap = Number(caps.CONCESSIONAL) || 32500;
  const nonConcessionalCap = Number(caps.NON_CONCESSIONAL) || 130000;
  const downsizerCap = Number(caps.DOWNSIZER?.MAX_CONTRIBUTION_PER_PERSON) || 300000;
  const tsbCutoff = Number(caps.TOTAL_BALANCE_THRESHOLD) || Number(FinancialConfig.TSB_THRESHOLDS?.NON_CONCESSIONAL_CUTOFF) || 2100000;
  const minDownsizerAge = Number(caps.DOWNSIZER?.MIN_AGE) || 55;
  const contributionCutoffAge = Number(FinancialConfig.AGE_THRESHOLDS?.CONTRIBUTION_CUTOFF_AGE) || 75;

  const byMember = {
    you: { scheduled: 0, concessional: Number(sgA) || 0, nonConcessional: 0, downsizer: 0, spouse: 0, sg: Number(sgA) || 0 },
    partner: { scheduled: 0, concessional: Number(sgB) || 0, nonConcessional: 0, downsizer: 0, spouse: 0, sg: Number(sgB) || 0 },
  };

  for (const row of scheduledContributions) {
    const member = row.recipient === CONTRIBUTION_RECIPIENTS.PARTNER && isCouple ? 'partner' : 'you';
    const amount = Number(row.amount) || 0;
    byMember[member].scheduled += amount;
    if (row.contributionType === CONTRIBUTION_TYPES.CONCESSIONAL) byMember[member].concessional += amount;
    else if (row.contributionType === CONTRIBUTION_TYPES.DOWNSIZER) byMember[member].downsizer += amount;
    else if (row.contributionType === CONTRIBUTION_TYPES.SPOUSE) {
      byMember[member].spouse += amount;
      byMember[member].nonConcessional += amount;
    } else byMember[member].nonConcessional += amount;
  }

  const flags = [];
  const memberInputs = [
    { member: 'you', age: Number(yourAge) || 0, balance: Number(balA) || 0 },
    { member: 'partner', age: Number(partnerAge) || 0, balance: Number(balB) || 0 },
  ];

  for (const { member, age, balance } of memberInputs) {
    if (member === 'partner' && !isCouple) continue;
    const totals = byMember[member];
    const who = formatMember(member);
    if (totals.concessional > concessionalCap) {
      flags.push({
        severity: 'error',
        member,
        type: 'concessional_cap',
        message: `${who}: concessional contributions $${Math.round(totals.concessional).toLocaleString()} exceed the $${concessionalCap.toLocaleString()} cap (includes SG).`,
      });
    }
    if (totals.nonConcessional > nonConcessionalCap) {
      flags.push({
        severity: 'warning',
        member,
        type: 'non_concessional_cap',
        message: `${who}: non-concessional/spouse contributions $${Math.round(totals.nonConcessional).toLocaleString()} exceed the standard $${nonConcessionalCap.toLocaleString()} annual cap. Bring-forward rules may be required.`,
      });
    }
    if (totals.nonConcessional > 0 && balance >= tsbCutoff) {
      flags.push({
        severity: 'error',
        member,
        type: 'non_concessional_tsb',
        message: `${who}: non-concessional contributions are not generally allowed when total super balance is at or above $${tsbCutoff.toLocaleString()}.`,
      });
    }
    if (totals.downsizer > downsizerCap) {
      flags.push({
        severity: 'error',
        member,
        type: 'downsizer_cap',
        message: `${who}: downsizer contributions $${Math.round(totals.downsizer).toLocaleString()} exceed the $${downsizerCap.toLocaleString()} per-person cap.`,
      });
    }
    if (totals.downsizer > 0 && age < minDownsizerAge) {
      flags.push({
        severity: 'error',
        member,
        type: 'downsizer_age',
        message: `${who}: downsizer contributions generally require age ${minDownsizerAge}+ at contribution time.`,
      });
    }
    const nonSgScheduled = totals.scheduled;
    if (nonSgScheduled > 0 && age >= contributionCutoffAge) {
      flags.push({
        severity: 'warning',
        member,
        type: 'contribution_age',
        message: `${who}: personal contributions at age ${age}+ can be restricted by contribution acceptance and work-test rules.`,
      });
    }
  }

  return {
    byMember,
    totalScheduled: byMember.you.scheduled + (isCouple ? byMember.partner.scheduled : 0),
    totalContributions: byMember.you.scheduled + byMember.you.sg + (isCouple ? byMember.partner.scheduled + byMember.partner.sg : 0),
    flags,
  };
}

export function formatLumpSumContributionRow(row) {
  const amt = Math.round(Number(row.amount) || 0).toLocaleString();
  const recipient = row.recipient === CONTRIBUTION_RECIPIENTS.SPLIT ? 'split' : row.recipient === CONTRIBUTION_RECIPIENTS.PARTNER ? 'partner' : 'you';
  const type = row.contributionType.replace(/([A-Z])/g, ' $1').toLowerCase();
  const when = row.scheduleType === 'year' ? `plan year ${row.scheduleValue}` : `age ${row.scheduleValue}`;
  return `$${amt} ${type} to ${recipient} in ${when}`;
}
